/**
 * Created by tdunham on 6/20/2017.
 */

import {environment}        from '../../../environments/environment';
import {Query}              from "./query";

import {isNullOrUndefined}  from "util";


/**
 * A Query class that works with the Fantasy ReST API.
 */
export class AngularQuery extends Query {

	private hideMessages: boolean   = false;
	private restURL: string         = '';
	private use_params: boolean     = true;
	private use_buster: boolean     = true;


	/**
	 * Initialize the object and, optionally, set the ReST API URL.
	 * If the API URL is not provided, it reads the value from the
	 * "restURL" environment variable.
	 *
	 * @param {string} restURL  The ReST API URL ('http://localhost:5000')
	 */
	constructor(restURL: string = '') {

		super();

		if (isNullOrUndefined(restURL) || 0 === restURL.length) {

			this.restURL = environment.restURL;

		} else {

			this.restURL = restURL;

		}

	}


	/**
	 * Get the string this Query object represents.
	 *
	 * @param site       The host part of the query string, usually like 'https://somesite.com'
	 * @returns string   The URL based on these Query settings.
	 */
	getUrl(site: string = ''): string {

		let theUrl: string;

		if (isNullOrUndefined(site) || 0 === site.length) {

			theUrl = this.restURL + this.endpoint;

		} else {

			theUrl = site + this.endpoint;

		}

		// Add a cache busting parameter to the URL, if requested.
		if (this.use_buster) {

			this.query_params.push(

				{ key: 'cachebuster', value: Date.now().toString() },

			);

		}

		if (this.use_params) {

			// Must multiply the page count by the offset.
			let offset:number = 0;

			if (0 !== this.page) {

				offset = this.page * this.per_page;

			}

			this.query_params.push(

				{ key: 'rowcount', value: this.per_page.toString() },
				{ key: 'offset', value: offset.toString() },

			);

		}

		// Assemble the query string.
		for ( let x = 0; x < this.query_params.length; x++ ) {

			theUrl += ( 0 === x ) ? '?' : '&';
			theUrl += this.query_params[x]['key'];
			theUrl += '=';
			theUrl += this.query_params[x]['value'];

		}

		if (this.hideMessages) {

			if ( 0 === this.query_params.length ) {

				theUrl += '?hideMessages';

			} else {

				theUrl += '&hideMessages';

			}

		}

		return theUrl;

	}


	/**
	 * Whether to display HTTP error messages
	 * in the main toolbar.  This defaults to
	 * false, but errors can be suppressed by
	 * setting this to true.
	 *
	 * @param {boolean} messages
	 */
	hideToolbarHttpMessages(messages: boolean): void {

		this.hideMessages = messages;

	}


	/**
	 * Implementation of abstract class.
	 */
	setEndpoints() {

		this.endpoints = [

			AngularQuery.ENDPOINT_COLLEGE,
			AngularQuery.ENDPOINT_NEWS,
			AngularQuery.ENDPOINT_NEWS_PARSER,
      AngularQuery.ENDPOINT_MASSROSTER_UPDATE,
			AngularQuery.ENDPOINT_NEWS_PLAYER,
			AngularQuery.ENDPOINT_NEWS_SEARCH,
			AngularQuery.ENDPOINT_NEWS_SEARCH_FULLTEXT,
			AngularQuery.ENDPOINT_NEWS_SEARCH_PLAYER,
			AngularQuery.ENDPOINT_NEWS_SEARCH_SOURCE,
			AngularQuery.ENDPOINT_NEWS_SEARCH_TEAM,
			AngularQuery.ENDPOINT_NEWS_SOURCE,
			AngularQuery.ENDPOINT_NEWS_TAGS,
			AngularQuery.ENDPOINT_NEWS_TEAM,
			AngularQuery.ENDPOINT_NFL_PLAYER_NEWS,
			AngularQuery.ENDPOINT_PLAYER,
			AngularQuery.ENDPOINT_PLAYER_SEARCH,
			AngularQuery.ENDPOINT_POSITION,
			AngularQuery.ENDPOINT_RSSMANAGER,
			AngularQuery.ENDPOINT_SEARCH_PHRASES,
			AngularQuery.ENDPOINT_SOURCE,
			AngularQuery.ENDPOINT_SPORT,
			AngularQuery.ENDPOINT_SPORT_CHECKBOXES,
			AngularQuery.ENDPOINT_TAG,
			AngularQuery.ENDPOINT_TEAM,
			AngularQuery.ENDPOINT_TEAM_DIVISION,
			AngularQuery.ENDPOINT_TEAM_LEAGUE,
			AngularQuery.ENDPOINT_USER,
			AngularQuery.ENDPOINT_USER_GET_INFO,
			AngularQuery.ENDPOINT_USER_LOGIN,
			AngularQuery.ENDPOINT_USER_LOGOUT,
			AngularQuery.ENDPOINT_USER_REPORT,
			AngularQuery.ENDPOINT_USER_TRACK,
			AngularQuery.ENDPOINT_VENDOR,
			AngularQuery.ENDPOINT_VERSION

		];

	}


	/**
	 * Implementation of abstract class.
	 */
	setPageValues() {
		// All the values are correct; do nothing here.
	}


	/**
	 * Should the cachebuster be used?
	 * The default behavior is true.  Set it to false to
	 * cancel its use for this query.
	 *
	 * @param {boolean} use  True if you want to use it; false otherwise.
	 */
	useCacheBuster(use: boolean): void {

		this.use_buster = use;

	}


	/**
	 * Should parameter values be passed with this
	 * request?
	 *
	 * @param {boolean} use  True if you want to use them; false otherwise.
	 */
	useParams(use: boolean): void {

		this.use_params = use;

	}


	// Constants for all the possible Angular REST-API tables we can access.
	public static readonly ENDPOINT_COLLEGE                 = '/fantasy/college/';
	public static readonly ENDPOINT_NEWS                    = '/fantasy/news/';
	public static readonly ENDPOINT_NEWS_PARSER             = '/fantasy/news/parser/';
    public static readonly ENDPOINT_MASSROSTER_UPDATE       = '/fantasy/MassPlayer/';
	public static readonly ENDPOINT_NEWS_PLAYER             = '/fantasy/news/player/';
	public static readonly ENDPOINT_NEWS_SEARCH             = '/fantasy/news/search/';
	public static readonly ENDPOINT_NEWS_SEARCH_FULLTEXT    = '/fantasy/news/search/fulltext/';
	public static readonly ENDPOINT_NEWS_SEARCH_PLAYER      = '/fantasy/news/search/player/';
	public static readonly ENDPOINT_NEWS_SEARCH_SOURCE      = '/fantasy/news/search/source/';
	public static readonly ENDPOINT_NEWS_SEARCH_TEAM        = '/fantasy/news/search/team/';
	public static readonly ENDPOINT_NEWS_SOURCE             = '/fantasy/news/source/';
	public static readonly ENDPOINT_NEWS_TAGS               = '/fantasy/news/tags/';
	public static readonly ENDPOINT_NEWS_TEAM               = '/fantasy/news/team/';
	public static readonly ENDPOINT_NFL_PLAYER_NEWS         = '/fantasy/nflplayernews/';
	public static readonly ENDPOINT_PLAYER                  = '/fantasy/player/';
	public static readonly ENDPOINT_PLAYER_SEARCH           = '/fantasy/player/search/';
	public static readonly ENDPOINT_POSITION                = '/fantasy/position/';
	public static readonly ENDPOINT_RSSMANAGER              = '/fantasy/rssmanager/';
	public static readonly ENDPOINT_SEARCH_PHRASES          = '/fantasy/search_phrases/';
	public static readonly ENDPOINT_SOURCE                  = '/fantasy/source/';
	public static readonly ENDPOINT_SPORT                   = '/fantasy/sport/';
	public static readonly ENDPOINT_SPORT_CHECKBOXES        = '/fantasy/sport/checkboxes/';
	public static readonly ENDPOINT_TAG                     = '/fantasy/tag/';
	public static readonly ENDPOINT_TEAM                    = '/fantasy/team/';
	public static readonly ENDPOINT_TEAM_DIVISION           = '/fantasy/team/division/';
	public static readonly ENDPOINT_TEAM_LEAGUE             = '/fantasy/team_league/';
	public static readonly ENDPOINT_USER                    = '/fantasy/user/';
	public static readonly ENDPOINT_USER_GET_INFO           = '/fantasy/user/get_info';
	public static readonly ENDPOINT_USER_LOGIN              = '/fantasy/user/login';
	public static readonly ENDPOINT_USER_LOGOUT             = '/fantasy/user/logout';
	public static readonly ENDPOINT_USER_REPORT             = '/fantasy/user/report/';
	public static readonly ENDPOINT_USER_TRACK              = '/fantasy/user/track/';
	public static readonly ENDPOINT_VENDOR                  = '/fantasy/vendor/';
	public static readonly ENDPOINT_VERSION                 = '/fantasy/version/';


}



// WEBPACK FOOTER //
// ./src/app/services/query/angularquery.ts